'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

import { Callout } from 'fumadocs-ui/components/callout';

import { isLocale, type Locale } from '@/shared/i18n';
import { reportErrorChainToConsole } from '@/shared/browser/error-chain-report';

/**
 * [lang] 分段错误边界：使用 fumadocs 官方模板的 Callout 错误样式，
 * 完整错误链由浏览器控制台抛出，不占页面版面。
 */

const langErrorCopy: Record<Locale, { title: string; description: string; retry: string }> = {
  zh: {
    title: '页面出现错误',
    description: '页面渲染时发生错误，完整错误信息已输出到浏览器控制台。',
    retry: '重试',
  },
  en: {
    title: 'Error',
    description: 'Something went wrong while rendering this page. Full error details are available in the browser console.',
    retry: 'Try Again',
  },
};

/** 从当前路径解析语言段，未命中语言时回退默认中文。 */
function resolveLangFromPath(pathname: string | null): Locale {
  const segment = pathname?.split('/')[1] ?? '';
  return isLocale(segment) ? segment : 'zh';
}

export default function LangErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const pathname = usePathname();
  const copy = langErrorCopy[resolveLangFromPath(pathname)];

  useEffect(() => {
    reportErrorChainToConsole(error);
  }, [error]);

  return (
    <main className="lang-error-panel flex flex-1 flex-col items-center justify-center gap-4 p-8">
      <Callout type="error" title={copy.title}>
        {copy.description}
      </Callout>
      <button
        type="button"
        onClick={() => reset()}
        className="lang-error-retry rounded border px-4 py-2 text-sm"
      >
        {copy.retry}
      </button>
    </main>
  );
}
